/**
 * Mémoire locale des liens d'écran Clock.
 *
 * Le lien est `/clock/:id#<token>` et le serveur n'en garde qu'une empreinte
 * (`accessTokenHash`) plus les six derniers caractères. Il est donc, par
 * conception, impossible à retrouver côté serveur : jusqu'ici il n'existait
 * qu'une fois, copié en silence dans le presse-papiers à la création, et perdu
 * dès que l'opérateur fermait la page.
 *
 * On conserve donc le secret là où il est déjà allé — le poste de l'opérateur —
 * pour pouvoir le recopier ou l'ouvrir plus tard. Cela n'élargit aucun accès :
 * ce navigateur avait déjà le lien en main. Un lien créé depuis un autre poste
 * reste inconnu ici, et l'interface doit le dire plutôt que de faire croire à
 * une perte.
 */

const STORAGE_KEY = 'trafficplay.clock.launchLinks'

type LinkMap = Record<string, string>

function read(): LinkMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const entries = Object.entries(parsed as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0)
    return Object.fromEntries(entries)
  } catch {
    // Mode kiosque, stockage plein ou JSON corrompu : on repart d'une mémoire
    // vide plutôt que de casser l'écran d'administration.
    return {}
  }
}

function write(map: LinkMap): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* stockage indisponible : la session courante fonctionne quand même */
  }
}

/** Chemin retenu pour un écran, ou `null` si ce poste ne l'a jamais vu. */
export function readLaunchPath(displayId: string): string | null {
  return read()[displayId] ?? null
}

/** Retient le lien qui vient d'être délivré, en remplaçant un lien révoqué. */
export function rememberLaunchPath(displayId: string, launchUrl: string): void {
  const map = read()
  map[displayId] = launchUrl
  write(map)
}

/** Oublie un écran supprimé, pour ne pas garder un secret sans objet. */
export function forgetLaunchPath(displayId: string): void {
  const map = read()
  if (!(displayId in map)) return
  delete map[displayId]
  write(map)
}

/**
 * Purge les écrans qui n'existent plus côté serveur. Appelée au chargement de
 * la liste : un lien orphelin n'ouvre rien et n'a aucune raison de survivre.
 */
export function pruneLaunchPaths(knownDisplayIds: readonly string[]): void {
  const map = read()
  const known = new Set(knownDisplayIds)
  let changed = false
  for (const id of Object.keys(map)) {
    if (known.has(id)) continue
    delete map[id]
    changed = true
  }
  if (changed) write(map)
}

/** Lien absolu, prêt à être copié ou ouvert. */
export function toAbsoluteLaunchUrl(launchPath: string, origin: string): string {
  return `${origin}${launchPath}`
}
